import { useState } from "react";

const days = [
{
day: "Monday",
focus: "HTML & CSS",
emoji: "🎨",
color: "from-orange-500 to-pink-500",
bg: "bg-orange-50",
border: "border-orange-200",
badge: "bg-orange-100 text-orange-700",
tasks: [
{ time: "0–10 min", label: "Learn", desc: "Watch 1 short HTML/CSS tutorial (freeCodeCamp, W3Schools, or YouTube)" },
{ time: "10–25 min", label: "Build", desc: "Recreate a simple webpage element — a navbar, card, or button from scratch" },
{ time: "25–30 min", label: "Reflect", desc: "Write 2–3 sentences in a journal: what you learned, what confused you" },
],
tip: "Start by building a simple personal profile page — it teaches layout, text, colors, and links all at once.",
resource: "freeCodeCamp Responsive Web Design",
},
{
day: "Tuesday", focus: "JavaScript Basics", emoji: "⚡",
bg: "bg-yellow-50", border: "border-yellow-200", badge: "bg-yellow-100 text-yellow-700",
tasks: [
{ time: "0–10 min", label: "Learn", desc: "Study one JS concept: variables, functions, arrays, or loops (one per week)" },
{ time: "10–25 min", label: "Practice", desc: "Solve 1-2 beginner challenges on Codewars (8kyu level)" },
{ time: "25–30 min", label: "Reflect", desc: "Type out what you learned in your own words - no copy-paste" },
],
tip: "Don’t rush through JS. Master one concept fully before moving on. Consistency beats speed.",
resource: "javascript.info - The Modern JavaScript Tutorial",
},
{
day: "Wednesday", focus: "Backend & APIs", emoji: "🔧",
bg: "bg-green-50", border: "border-green-200", badge: "bg-green-100 text-green-700",
tasks: [
{ time: "0–10 min", label: "Learn", desc: "Watch a beginner intro to Node.js, Python Flask, or how APIs work" },
{ time: "10–25 min", label: "Explore", desc: "Use a free public API (like Open Trivia DB or Weather API) in Postman" },
{ time: "25–30 min", label: "Note", desc: "Write down what a request and response looks like - draw it out if it helps" },
],
tip: "Think of APIs like a waiter - you make a request, they go to the kitchen (server), and bring back data.",
resource: "Postman Learning Center (free)",
},
{
day: "Thursday", focus: "Mini Project Day", emoji: "🚀",
bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-700",
tasks: [
{ time: "0–5 min", label: "Plan", desc: "Pick a tiny project: to-do list, calculator, weather widget, or quiz app" },
{ time: "5–25 min", label: "Build", desc: "Work on it using what you’ve learned Mon-Wed. Don’t aim for perfect." },
{ time: "25–30 min", label: "Push", desc: "Save your code to GitHub - even incomplete work counts!" },
],
tip: "Tiny projects are your portfolio. After 1 month you’ll have 4+ projects to show people.",
resource: "GitHub - create a free account and push your code",
},
{
day: "Friday", focus: "DevOps & Tools", emoji: "☁️",
bg: "bg-purple-50", border: "border-purple-200", badge: "bg-purple-100 text-purple-700",
tasks: [
{ time: "0–10 min", label: "Learn", desc: "Study one DevOps concept: Git commands, terminal basics, Docker intro, or CI/CD" },
{ time: "10–25 min", label: "Practice", desc: "Open your terminal and practice: mkdir, cd, git init, git commit, git push" },
{ time: "25–30 min", label: "Read", desc: "Skim one DevOps article or watch a short explainer on YouTube" },
],
tip: "Git is non-negotiable. Learn it early. Every developer uses it every single day.",
resource: "Oh My Git! - free interactive Git learning game",
},
{
day: "Saturday", focus: "Review & Debug", emoji: "🔍",
bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-700",
tasks: [
{ time: "0–15 min", label: "Review", desc: "Go back to this week’s code - read it line by line and understand each part" },
{ time: "15–25 min", label: "Improve", desc: "Refactor one small thing: rename a variable, simplify a function, or fix a bug" },
{ time: "25–30 min", label: "Community", desc: "Read 2-3 posts on Dev.to or Stack Overflow - just browsing counts!" },
],
tip: "Reading other people’s code is one of the fastest ways to improve. Don’t skip this day.",
resource: "Dev.to - developer community and articles",
},
{
day: "Sunday", focus: "Rest & Explore", emoji: "🌱",
bg: "bg-teal-50", border: "border-teal-200", badge: "bg-teal-100 text-teal-700",
tasks: [
{ time: "0–10 min", label: "Inspire", desc: "Browse Dribbble, Awwwards, or GitHub Trending - see what others are building" },
{ time: "10–20 min", label: "Plan", desc: "Write next week’s mini project idea - something slightly harder than this week’s" },
{ time: "20–30 min", label: "Rest", desc: "This is optional - your brain needs rest to consolidate what it learned!" },
],
tip: "Rest is part of learning. Burnout is real. One relaxed day makes the other 6 more effective.",
resource: "GitHub Trending - see what devs are building right now",
},
];

const milestones = {
1: [
{ week: "Week 1", goal: "Build a basic HTML/CSS personal profile page and push it to GitHub", icon: "🎨" },
{ week: "Week 2", goal: "Add JavaScript - make one element on your page interactive (button, form)", icon: "⚡" },
{ week: "Week 3", goal: "Fetch data from a public API and display it on your page", icon: "🔧" },
{ week: "Week 4", goal: "Deploy your project live using GitHub Pages or Netlify - show the world!", icon: "🚀" },
],
2: [
{ week: "Week 5", goal: "Build a multi-page React app using React Router (Home, About, Contact pages)", icon: "⚛️" },
{ week: "Week 6", goal: "Connect your React app to a real live API - display dynamic data like weather or news", icon: "🌐" },
{ week: "Week 7", goal: "Build a simple backend server with Node.js and Express - create your first API endpoint", icon: "🖥️" },
{ week: "Week 8", goal: "Connect your frontend to your own backend and deploy the full stack app online", icon: "🏆" },
],
};

const month2Skills = [
{ title: "React Components", desc: "Break UI into reusable pieces", emoji: "⚛️", color: "bg-blue-900 border-blue-700" },
{ title: "React Router", desc: "Add multiple pages without reloading", emoji: "🗺️", color: "bg-indigo-900 border-indigo-700" },
{ title: "useState & useEffect", desc: "Manage data in your components", emoji: "🪝", color: "bg-purple-900 border-purple-700" },
{ title: "Node.js & Express", desc: "Build your own server from scratch", emoji: "🖥️", color: "bg-green-900 border-green-700" },
{ title: "REST APIs", desc: "Design GET, POST, PUT, DELETE routes", emoji: "🔁", color: "bg-teal-900 border-teal-700" },
{ title: "Full Stack Deploy", desc: "Host frontend and backend live", emoji: "🌍", color: "bg-orange-900 border-orange-700" },
];

export default function DailyPlan() {
const [selected, setSelected] = useState(0);
const [checked, setChecked] = useState({});
const [activeMonth, setActiveMonth] = useState(1);

const toggleCheck = (dayIdx, taskIdx) => {
const key = `${dayIdx}-${taskIdx}`;
setChecked(prev => ({ ...prev, [key]: !prev[key] }));
};

const d = days[selected];
const completedToday = d.tasks.filter((_, i) => checked[`${selected}-${i}`]).length;

return (
<div className="min-h-screen bg-gray-950 text-white p-4 font-sans">
<div className="max-w-2xl mx-auto">


    {/* Header */}
    <div className="text-center mb-6">
      <h1 className="text-2xl font-bold text-white">🧠 Dev Daily Plan</h1>
      <p className="text-gray-400 text-sm mt-1">30 min/day · Beginner · Full Stack + DevOps</p>
    </div>

    {/* Day Selector */}
    <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
      {days.map((d, i) => (
        <button key={i} onClick={() => setSelected(i)}
          className={`flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-xl border transition-all ${
            selected === i ? "bg-white text-gray-900 border-white shadow-lg scale-105"
              : "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500"
          }`}>
          <span className="text-lg">{d.emoji}</span>
          <span className="text-xs font-semibold mt-0.5">{d.day.slice(0, 3)}</span>
        </button>
      ))}
    </div>

    {/* Day Card */}
    <div className={`rounded-2xl border ${d.border} ${d.bg} p-5 mb-5`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{d.emoji}</span>
            <h2 className="text-xl font-bold text-gray-800">{d.day}</h2>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${d.badge}`}>{d.focus}</span>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-gray-700">{completedToday}/{d.tasks.length}</div>
          <div className="text-xs text-gray-500">tasks done</div>
        </div>
      </div>

      <div className="space-y-3">
        {d.tasks.map((task, i) => {
          const key = `${selected}-${i}`;
          return (
            <div key={i} onClick={() => toggleCheck(selected, i)}
              className={`flex gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                checked[key] ? "bg-white border-gray-200 opacity-60" : "bg-white border-gray-100 hover:border-gray-300"
              }`}>
              <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                checked[key] ? "bg-green-500 border-green-500" : "border-gray-300"
              }`}>
                {checked[key] && <span className="text-white text-xs">✓</span>}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold text-gray-500">{task.time}</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${d.badge}`}>{task.label}</span>
                </div>
                <p className={`text-sm text-gray-700 ${checked[key] ? "line-through" : ""}`}>{task.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-white rounded-xl border border-gray-200">
        <p className="text-xs font-bold text-gray-500 mb-1">💡 PRO TIP</p>
        <p className="text-sm text-gray-700">{d.tip}</p>
      </div>
      <div className="mt-3 p-3 bg-white rounded-xl border border-gray-200">
        <p className="text-xs font-bold text-gray-500 mb-1">📚 TODAY'S RESOURCE</p>
        <p className="text-sm text-gray-700">{d.resource}</p>
      </div>
    </div>

    {/* Milestones Section */}
    <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5 mb-5">
      <h3 className="font-bold text-white mb-4">🏆 Milestones</h3>

      {/* Month Toggle */}
      <div className="flex gap-2 mb-4">
        {[1, 2].map(m => (
          <button key={m} onClick={() => setActiveMonth(m)}
            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
              activeMonth === m ? "bg-white text-gray-900" : "bg-gray-700 text-gray-400 hover:bg-gray-600"
            }`}>
            Month {m} {m === 1 ? "- Foundations" : "- Level Up 🔥"}
          </button>
        ))}
      </div>

      {activeMonth === 2 && (
        <div className="mb-4 p-3 rounded-xl bg-gray-900 border border-blue-700">
          <p className="text-xs font-bold text-blue-300 mb-1">🎯 MONTH 2 FOCUS</p>
          <p className="text-sm text-blue-100">You have got the basics. Now you are going full stack - React, Node.js, and your first deployed backend!</p>
        </div>
      )}

      <div className="space-y-3">
        {milestones[activeMonth].map((m, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0 text-lg">
              {m.icon}
            </div>
            <div>
              <p className="text-xs font-bold text-gray-400">{m.week}</p>
              <p className="text-sm text-gray-200">{m.goal}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Month 2 Skills */}
    {activeMonth === 2 && (
      <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5">
        <h3 className="font-bold text-white mb-1">⚡ Skills You Will Learn in Month 2</h3>
        <p className="text-gray-400 text-xs mb-4">Tools that real full stack developers use every day</p>
        <div className="grid grid-cols-2 gap-2">
          {month2Skills.map((s, i) => (
            <div key={i} className={`p-3 rounded-xl border ${s.color}`}>
              <div className="text-xl mb-1">{s.emoji}</div>
              <p className="text-xs font-bold text-white mb-0.5">{s.title}</p>
              <p className="text-xs text-gray-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    <p className="text-center text-gray-600 text-xs mt-4">Tap tasks to mark complete · Switch months to see your roadmap</p>
  </div>
</div>


);
}